'use client';

import { useState, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ScanResult {
  name: string;
  dosage: string;
  prescribedBy: string;
  instructions: string;
  refillDate: string;
}

interface ScannerInterfaceProps {
  onScanComplete: (result: ScanResult) => void;
  onCancel: () => void;
}

export default function ScannerInterface({
  onScanComplete,
  onCancel,
}: ScannerInterfaceProps) {
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageData = e.target?.result as string;
      setPreviewImage(imageData);
      analyzeMedicationImage(imageData, file.type);
    };
    reader.readAsDataURL(file);
  };

  const analyzeMedicationImage = async (imageData: string, mimeType: string) => {
    setIsScanning(true);
    setError(null);

    try {
      const response = await fetch('/api/gemini/analyze-medication', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageData,
          mimeType,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error.message || 'Failed to analyze image');
        setIsScanning(false);
        return;
      }

      // Map Gemini response to ScanResult format
      const medicationResult: ScanResult = {
        name: result.data.name,
        dosage: result.data.dosage,
        prescribedBy: result.data.prescribedBy || 'Dr. Robert Chen',
        instructions: result.data.instructions || result.data.frequency || 'As directed',
        refillDate: result.data.refillDate || '',
      };

      setScanResult(medicationResult);
      setIsScanning(false);
    } catch (err) {
      console.error('Error analyzing medication image:', err);
      setError('Failed to connect to AI service. Please try again.');
      setIsScanning(false);
    }
  };

  const handleConfirm = () => {
    if (scanResult) {
      onScanComplete(scanResult);
    }
  };

  const handleRetry = () => {
    setScanResult(null);
    setPreviewImage(null);
    setIsScanning(false);
    setError(null);
  };

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl glass rounded-xl overflow-hidden">
        <div className="p-6 border-b border-sage-200/30">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-heading font-semibold text-foreground">
              Scan Medication
            </h2>
            <button
              onClick={onCancel}
              className="p-2 rounded-lg hover:bg-muted transition-smooth"
              aria-label="Close scanner"
            >
              <Icon name="XMarkIcon" size={24} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {!previewImage && !scanResult && (
            <div className="space-y-6">
              <div className="aspect-video bg-sage-50 rounded-lg border-2 border-dashed border-sage-300 flex flex-col items-center justify-center gap-4 p-8">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="CameraIcon" size={40} className="text-primary" />
                </div>
                <div className="text-center">
                  <p className="text-lg font-caption font-medium text-foreground mb-2">
                    Capture Pill Bottle
                  </p>
                  <p className="text-sm font-caption text-muted-foreground">
                    Position the prescription label clearly in frame
                  </p>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />

              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-4 rounded-lg bg-primary text-primary-foreground text-base font-caption font-medium hover:bg-primary/90 transition-smooth flex items-center justify-center gap-2"
              >
                <Icon name="CameraIcon" size={20} />
                Take Photo
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-sage-200"></div>
                <span className="text-sm font-caption text-muted-foreground">or</span>
                <div className="flex-1 h-px bg-sage-200"></div>
              </div>

              <button
                onClick={onCancel}
                className="w-full py-4 rounded-lg bg-muted text-foreground text-base font-caption font-medium hover:bg-muted/80 transition-smooth"
              >
                Enter Manually
              </button>
            </div>
          )}

          {previewImage && !scanResult && (
            <div className="space-y-6">
              <div className="aspect-video bg-sage-50 rounded-lg overflow-hidden">
                <AppImage
                  src={previewImage}
                  alt="Captured pill bottle showing prescription label with medication details"
                  className="w-full h-full object-cover"
                />
              </div>

              {isScanning && (
                <div className="flex flex-col items-center gap-4 py-8">
                  <div className="w-16 h-16 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                  <p className="text-base font-caption font-medium text-foreground">
                    Analyzing prescription label...
                  </p>
                  <p className="text-sm font-caption text-muted-foreground">
                    Using AI to extract medication details
                  </p>
                </div>
              )}

              {error && (
                <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                  <div className="flex items-start gap-3">
                    <Icon
                      name="ExclamationTriangleIcon"
                      size={20}
                      className="text-destructive flex-shrink-0 mt-0.5"
                      variant="solid"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-caption font-medium text-destructive mb-2">
                        {error}
                      </p>
                      <button
                        onClick={handleRetry}
                        className="text-sm font-caption font-medium text-primary hover:underline"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {scanResult && (
            <div className="space-y-6">
              <div className="p-6 rounded-lg bg-success/10 border border-success/20">
                <div className="flex items-start gap-3 mb-4">
                  <Icon
                    name="CheckCircleIcon"
                    size={24}
                    className="text-success flex-shrink-0"
                    variant="solid"
                  />
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                      Scan Successful
                    </h3>
                    <p className="text-sm font-caption text-muted-foreground">
                      Review the extracted information below
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-caption text-muted-foreground mb-1">
                      Medication Name
                    </p>
                    <p className="text-base font-caption font-medium text-foreground">
                      {scanResult.name}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-caption text-muted-foreground mb-1">
                      Dosage
                    </p>
                    <p className="text-base font-caption font-medium text-foreground">
                      {scanResult.dosage}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-caption text-muted-foreground mb-1">
                      Prescribed By
                    </p>
                    <p className="text-base font-caption font-medium text-foreground">
                      {scanResult.prescribedBy}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-caption text-muted-foreground mb-1">
                      Instructions
                    </p>
                    <p className="text-base font-caption font-medium text-foreground">
                      {scanResult.instructions}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-caption text-muted-foreground mb-1">
                      Refill Date
                    </p>
                    <p className="text-base font-caption font-medium text-foreground">
                      {scanResult.refillDate}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleRetry}
                  className="flex-1 py-3 rounded-lg bg-muted text-foreground text-base font-caption font-medium hover:bg-muted/80 transition-smooth"
                >
                  Scan Again
                </button>
                <button
                  onClick={handleConfirm}
                  className="flex-1 py-3 rounded-lg bg-primary text-primary-foreground text-base font-caption font-medium hover:bg-primary/90 transition-smooth"
                >
                  Add Medication
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}